import {
  ConsoleOutputLevel,
  DepID_SentinelDetection,
  OutputToConsoleFunc,
  SessionMetadata,
} from "../@types"
import { ApplicationEvents } from "./applicationEvents"
import makeExecutor, { PlatformTestObjectResultBundle } from "./makeExecutor"
import { Scheduler } from "./measurementScheduler"
import { PerformanceEntryManager } from "./openinsights/resourceTiming"
import { PlatformTestObject } from "./platformTestObject"
import { ResettableEventCounter } from "./resettableEventCounter"
import { RuntimeValues } from "./runtimeValues"
import { SessionConfig } from "./sessionConfig"
import { HasValue } from "../@types"
import sequence from "./openinsights/promiseSequence"

export interface SentinelDetectionDependencies {
  applicationEvents: ApplicationEvents
  callClearTimeout: (depID: number, timeoutID: number) => void
  callFetch: (url: string, options?: RequestInit) => Promise<Response>
  callSendBeacon: (depID: number, url: string) => boolean
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearResourceTimings: () => void
  maxTargetFrequency: HasValue<number>
  measurementScheduler: Scheduler
  newAbortController: () => AbortController
  newDate: (depID: number) => Date
  newEpochTimestamp: (depID: number) => number
  newMonotonicTimestamp: (depID: number) => number
  newRandomNumber: (depID: number) => number
  outputToConsole: OutputToConsoleFunc
  performanceEntryAwaitTimeouts: ResettableEventCounter
  performanceEntryManager: PerformanceEntryManager
  runtimeValues: RuntimeValues
}

export interface SentinelDetectionResult {
  /**
   * Whether detection actually executed end-to-end. True only when the
   * user was sampled AND the CDN-diversity safety gate passed (#849) AND
   * `executeSentinelDetection` ran. False on the not-configured,
   * not-sampled, and gate-skip paths.
   *
   * Pre-#849 this was equivalent to "user was sampled". The diversity
   * gate split that equivalence, so consumers that need the
   * was-sampled signal specifically should not rely on this field.
   */
  detectionAttempted: boolean
  /**
   * Whether blocking is detected. The meaning varies by path:
   *
   * - `detectionAttempted = true`: the just-computed result for this
   *   cycle (`failureRate >= threshold`).
   * - `detectionAttempted = false` via the diversity-gate skip path
   *   (#849): the *preserved* prior value of
   *   `runtimeValues.trackerBlockingDetected` (skip-and-preserve
   *   semantics, so a mid-session degradation in available CDN
   *   diversity does not "unblock" a legitimately blocked session).
   * - `detectionAttempted = false` via not-configured / not-sampled:
   *   `false` (no prior state is consulted).
   */
  blockingDetected: boolean
  /** Number of sentinel tests executed */
  sentinelCount: number
  /** Number of sentinel tests that failed */
  failureCount: number
  /** Calculated failure rate (failureCount / sentinelCount) */
  failureRate: number
  /** Threshold used for detection */
  threshold: number
  /** Platforms that were used as sentinels */
  sentinelPlatforms: PlatformTestObject[]
}

/**
 * Minimum number of distinct CDN providers that must be represented in the
 * selected sample for sentinel detection to run (#849 CDN-diversity safety
 * gate). At the current 0.7 failure threshold, a single-CDN outage in a
 * 4-CDN sample produces at most a 25% failure rate -- safely below the
 * threshold. Hardcoded rather than server-driven so a misconfigured server
 * cannot disable the structural defense; revisit alongside any future
 * scrutiny of the failure threshold (see follow-up tracked in #849).
 */
export const MIN_DISTINCT_CDNS = 4

const newDetectionNotConfiguredMessage = () =>
  "Sentinel detection not configured - skipping detection"

const newDetectionNotSampledMessage = (sampleRate: number) =>
  `User not selected for sentinel detection (sample rate: ${sampleRate})`

const newDetectionSampledMessage = (sampleRate: number) =>
  `User selected for sentinel detection (sample rate: ${sampleRate})`

const newInsufficientDiversityMessage = (
  distinctCDNs: number,
  sampleSize: number,
) =>
  `Skipping sentinel detection: insufficient CDN diversity ` +
  `(${distinctCDNs} distinct CDNs across ${sampleSize} sampled platforms; ` +
  `minimum required: ${MIN_DISTINCT_CDNS})`

const newDetectionResultsMessage = (
  failed: number,
  total: number,
  rate: number,
  threshold: number,
) =>
  `Sentinel detection results: ${failed}/${total} failed ` +
  `(${(rate * 100).toFixed(1)}%, threshold: ${(threshold * 100).toFixed(1)}%)`

const newBlockingDetectedMessage = () =>
  "Tracker blocking detected - entering dormancy mode"

const newBlockingClearedMessage = () =>
  "Tracker blocking cleared - measurements will resume"

const newStateUpdatedMessage = (blockingDetected: boolean) =>
  `Tracker blocking state updated: ${blockingDetected ? "DORMANT" : "ACTIVE"}`

/**
 * Check if sentinel detection is fully configured
 */
function isSentinelDetectionConfigured(sessionConfig: SessionConfig): boolean {
  const settings = sessionConfig.siteOwnerSettings
  return (
    typeof settings.sentinelDetectionSampleRate === "number" &&
    settings.sentinelDetectionSampleRate > 0 &&
    typeof settings.sentinelDetectionFailureThreshold === "number" &&
    settings.sentinelDetectionFailureThreshold >= 0
  )
}

/**
 * Extract sentinel platforms from the population
 */
function extractSentinelPlatforms(
  population: PlatformTestObject[],
): PlatformTestObject[] {
  return population.filter((platform) => platform.sentinelPlatform === true)
}

/**
 * Parse the CDN identifier from a Doppler platform ID. Convention is the
 * three-token form `<owner>:<product>:<cdn>` (see docs/src/platforms.md).
 * Returns null for non-conforming IDs so callers can exclude them from the
 * diversity count rather than treating them as a stand-in CDN.
 */
export function extractCDN(platformID: string): string | null {
  const tokens = platformID.split(":")
  if (tokens.length < 3) {
    return null
  }
  const cdn = tokens[2]
  return cdn.length > 0 ? cdn : null
}

/**
 * Count the number of distinct CDN providers represented in a list of
 * platforms, using the `<owner>:<product>:<cdn>` convention. Platforms whose
 * IDs don't parse to a CDN are excluded from the count entirely (#849).
 */
export function countDistinctCDNs(platforms: PlatformTestObject[]): number {
  const cdns = new Set<string>()
  for (const platform of platforms) {
    const cdn = extractCDN(platform.dopplerPlatformID)
    if (cdn !== null) {
      cdns.add(cdn)
    }
  }
  return cdns.size
}

/**
 * Select up to `cap` sentinel platforms uniformly at random without
 * replacement from `eligible`. Treats the following inputs as "no cap"
 * (returns all eligible platforms in their original order, no shuffle,
 * no extra `newRandomNumber` calls):
 *
 * - `undefined` -- forward-compatible with pre-#849 server configs that
 *   omit `sdpc`.
 * - non-finite (`NaN`, `Infinity`) -- defends against upstream coercion
 *   bugs producing values that would otherwise silently select zero
 *   platforms (NaN) or fall through to the all-eligible branch
 *   (Infinity). Treating both as "no cap" makes the contract explicit.
 * - `<= 0` -- a non-positive cap is meaningless; degrade to legacy
 *   "probe everything" behavior rather than measuring nothing.
 * - `>= eligible.length` -- cap larger than the pool means every
 *   eligible platform is selected; skip the shuffle.
 *
 * Non-integer caps are floored before use, since the loop uses integer
 * indexing. Negative or fractional values larger than 1 produce the
 * floor-of-cap many draws.
 *
 * Implementation: in-place Fisher-Yates partial shuffle. Drawing `n`
 * samples requires `n` calls to `newRandomNumber`, regardless of population
 * size. Each platform has equal expected probability of selection per
 * cycle, so over many cycles every sentinel is measured equally for
 * tracker blocking detection in aggregate (#849 invariant).
 */
function selectSentinelPlatforms(
  eligible: PlatformTestObject[],
  cap: number | undefined,
  newRandomNumber: (depID: number) => number,
): PlatformTestObject[] {
  if (typeof cap !== "number" || !Number.isFinite(cap) || cap <= 0) {
    return eligible
  }
  const intCap = Math.floor(cap)
  if (intCap >= eligible.length) {
    return eligible
  }
  // Copy so we don't mutate the caller's array.
  const pool = eligible.slice()
  const result: PlatformTestObject[] = []
  for (let i = 0; i < intCap; i++) {
    const remaining = pool.length - i
    const j =
      i + Math.floor(newRandomNumber(DepID_SentinelDetection) * remaining)
    // Swap into the i-th slot.
    const tmp = pool[i]
    pool[i] = pool[j]
    pool[j] = tmp
    result.push(pool[i])
  }
  return result
}

/**
 * Execute sentinel detection tests and analyze results
 */
async function executeSentinelDetection(
  dependencies: SentinelDetectionDependencies,
  sessionMetadata: SessionMetadata,
  sentinelPlatforms: PlatformTestObject[],
): Promise<{
  failureCount: number
  total: number
  heldBeacons: Array<() => Promise<void>>
}> {
  if (sentinelPlatforms.length === 0) {
    return { failureCount: 0, total: 0, heldBeacons: [] }
  }

  // Create executors for sentinel measurements with beacon holding
  const sentinelExecutors = sentinelPlatforms.map((platform) =>
    makeExecutor(
      {
        applicationEvents: dependencies.applicationEvents,
        callClearTimeout: dependencies.callClearTimeout,
        callSendBeacon: dependencies.callSendBeacon,
        callSetTimeout: dependencies.callSetTimeout,
        callClearResourceTimings: dependencies.callClearResourceTimings,
        callFetch: dependencies.callFetch,
        maxTargetFrequency: dependencies.maxTargetFrequency,
        measurementScheduler: dependencies.measurementScheduler,
        newAbortController: dependencies.newAbortController,
        newDate: dependencies.newDate,
        newEpochTimestamp: dependencies.newEpochTimestamp,
        newMonotonicTimestamp: dependencies.newMonotonicTimestamp,
        newRandomNumber: dependencies.newRandomNumber,
        outputToConsole: dependencies.outputToConsole,
        performanceEntryAwaitTimeouts:
          dependencies.performanceEntryAwaitTimeouts,
        performanceEntryManager: dependencies.performanceEntryManager,
        runtimeValues: dependencies.runtimeValues,
      },
      platform,
      sessionMetadata,
      true, // holdBeacon = true for sentinels
    ),
  )

  // Use sequential execution instead of concurrent. This prevents
  // RegisteredCallbackNotConsumedError and false positive blocking detection
  dependencies.outputToConsole(
    ConsoleOutputLevel.debug,
    () =>
      `Executing ${sentinelExecutors.length} sentinel measurements ` +
      `sequentially to prevent Resource Timing conflicts`,
  )

  // Execute sentinel measurements sequentially instead of concurrently
  const testResults = await sequence(
    sentinelExecutors.map((executor) => () => executor.execute()),
  )

  // Collect held beacon functions and count failures
  const heldBeacons: Array<() => Promise<void>> = []
  let failureCount = 0

  testResults.forEach((result, index) => {
    if (!result) {
      dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () => `Sentinel ${index}: FAILED (undefined result)`,
      )
      failureCount++
      return
    }

    const testResult = result as PlatformTestObjectResultBundle & {
      heldBeacon?: () => Promise<void>
    }

    // Collect held beacon if present
    if (typeof testResult.heldBeacon === "function") {
      heldBeacons.push(testResult.heldBeacon)
    }

    // Create concise result summary
    const status = testResult.status || "no-status"
    const timeout = testResult.timeout ? "timeout" : "ok"
    const error = testResult.error ? "error" : "ok"
    const cdn = testResult.cdnResponseHeaderValue || "unknown-cdn"

    // Log the actual failure classification logic with concise info
    if (testResult.error) {
      dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () =>
          `Sentinel ${index}: FAILED (${status}, ${timeout}, ${error}, ${cdn})`,
      )
      failureCount++
      return
    }
    if (testResult.timeout === true) {
      dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () =>
          `Sentinel ${index}: FAILED (${status}, ${timeout}, ${error}, ${cdn})`,
      )
      failureCount++
      return
    }
    if (
      typeof testResult.status === "number" &&
      (testResult.status < 200 || testResult.status >= 300)
    ) {
      dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () =>
          `Sentinel ${index}: FAILED (${status}, ${timeout}, ${error}, ${cdn})`,
      )
      failureCount++
      return
    }

    dependencies.outputToConsole(
      ConsoleOutputLevel.debug,
      () =>
        `Sentinel ${index}: SUCCESS (${status}, ${timeout}, ${error}, ${cdn})`,
    )
  })

  return {
    failureCount,
    total: sentinelPlatforms.length,
    heldBeacons,
  }
}

/**
 * Perform tracker blocking detection
 */
export async function performSentinelDetection(
  dependencies: SentinelDetectionDependencies,
  sessionMetadata: SessionMetadata,
  population: PlatformTestObject[],
  sessionConfig: SessionConfig,
): Promise<SentinelDetectionResult> {
  const outputToConsole = dependencies.outputToConsole
  const runtimeValues = dependencies.runtimeValues

  // Check if detection is configured
  if (!isSentinelDetectionConfigured(sessionConfig)) {
    outputToConsole(ConsoleOutputLevel.info, () =>
      newDetectionNotConfiguredMessage(),
    )
    return {
      detectionAttempted: false,
      blockingDetected: false,
      sentinelCount: 0,
      failureCount: 0,
      failureRate: 0,
      threshold: 0,
      sentinelPlatforms: [],
    }
  }

  const settings = sessionConfig.siteOwnerSettings

  // Extract validated settings - only sample rate and threshold needed
  const sampleRate = settings.sentinelDetectionSampleRate!
  const failureThreshold = settings.sentinelDetectionFailureThreshold!

  // Check if user is sampled for detection
  const randomValue = dependencies.newRandomNumber(DepID_SentinelDetection)
  if (randomValue >= sampleRate) {
    outputToConsole(ConsoleOutputLevel.info, () =>
      newDetectionNotSampledMessage(sampleRate),
    )
    return {
      detectionAttempted: false,
      blockingDetected: false,
      sentinelCount: 0,
      failureCount: 0,
      failureRate: 0,
      threshold: failureThreshold,
      sentinelPlatforms: [],
    }
  }

  outputToConsole(ConsoleOutputLevel.info, () =>
    newDetectionSampledMessage(sampleRate),
  )

  // Bounded uniform selection (#849): cap the per-cycle probe count at
  // settings.sentinelDetectionProbeCount. Selection is uniform random
  // without replacement so each sentinel is equally likely to be measured
  // for blocking detection in any given cycle. When the cap is unset or
  // >= eligible population, all eligible sentinels are used (forward-
  // compatible with pre-#849 server configs).
  const eligibleSentinels = extractSentinelPlatforms(population)
  const sentinelPlatforms = selectSentinelPlatforms(
    eligibleSentinels,
    settings.sentinelDetectionProbeCount,
    dependencies.newRandomNumber,
  )

  // CDN-diversity safety gate (#849): only run detection when the selected
  // sample spans MIN_DISTINCT_CDNS or more distinct CDN providers. This
  // rules out single-CDN (and at the current threshold, two-CDN-
  // simultaneous) outages as a cause of an observed failure rate, which
  // would otherwise produce false-positive blocking detection and send the
  // session into dormancy. Skip-and-preserve semantics: the runtime
  // dormancy flag is left at its prior value rather than reset, so a
  // mid-session degradation in available diversity does not "unblock" a
  // legitimately blocked session.
  const distinctCDNs = countDistinctCDNs(sentinelPlatforms)
  if (distinctCDNs < MIN_DISTINCT_CDNS) {
    outputToConsole(ConsoleOutputLevel.info, () =>
      newInsufficientDiversityMessage(distinctCDNs, sentinelPlatforms.length),
    )
    return {
      detectionAttempted: false,
      blockingDetected: runtimeValues.trackerBlockingDetected,
      sentinelCount: 0,
      failureCount: 0,
      failureRate: 0,
      threshold: failureThreshold,
      sentinelPlatforms: [],
    }
  }

  outputToConsole(
    ConsoleOutputLevel.debug,
    () =>
      `Using ${sentinelPlatforms.length} of ${eligibleSentinels.length} ` +
      `eligible sentinel platforms for detection ` +
      `(${distinctCDNs} distinct CDNs)`,
  )

  // Update detection timestamp
  runtimeValues.lastTrackerDetectionAt = dependencies.newDate(
    DepID_SentinelDetection,
  )

  // Execute detection measurements with held beacons
  const { failureCount, total, heldBeacons } = await executeSentinelDetection(
    dependencies,
    sessionMetadata,
    sentinelPlatforms,
  )

  // Calculate failure rate and determine if blocking detected
  const failureRate = total > 0 ? failureCount / total : 0
  const blockingDetected = failureRate >= failureThreshold

  outputToConsole(ConsoleOutputLevel.info, () =>
    newDetectionResultsMessage(
      failureCount,
      total,
      failureRate,
      failureThreshold,
    ),
  )

  // Track previous state for transition logging
  const previouslyBlocked = runtimeValues.trackerBlockingDetected

  // Log state transitions only
  if (blockingDetected && !previouslyBlocked) {
    outputToConsole(ConsoleOutputLevel.info, () => newBlockingDetectedMessage())
  } else if (!blockingDetected && previouslyBlocked) {
    outputToConsole(ConsoleOutputLevel.info, () => newBlockingClearedMessage())
  }

  // IMPORTANT (#831): Update runtimeValues.trackerBlockingDetected *before*
  // flushing held beacons. The beacon-send path reads this flag and would
  // otherwise emit spurious "Sending beacon ... despite dormancy mode!"
  // warnings when recovering from a blocked state. Callers must not update
  // this flag themselves after performSentinelDetection returns.
  runtimeValues.trackerBlockingDetected = blockingDetected
  outputToConsole(ConsoleOutputLevel.info, () =>
    newStateUpdatedMessage(blockingDetected),
  )

  // CRITICAL: Decision point for held beacon data
  if (!blockingDetected && heldBeacons.length > 0) {
    // No blocking detected - send all held sentinel beacons
    outputToConsole(
      ConsoleOutputLevel.debug,
      () => `Sending ${heldBeacons.length} held sentinel measurement beacons`,
    )

    const results = await Promise.allSettled(
      heldBeacons.map((beacon) => beacon()),
    )

    // Check for any failures and log them
    const failures = results.filter((result) => result.status === "rejected")
    if (failures.length > 0) {
      const firstError = (failures[0] as PromiseRejectedResult).reason
      outputToConsole(
        ConsoleOutputLevel.warn,
        () =>
          `Error sending held sentinel beacons: ${
            firstError instanceof Error
              ? firstError.message
              : String(firstError)
          }`,
      )
    }
  } else if (blockingDetected && heldBeacons.length > 0) {
    // Blocking detected - discard all held beacon data
    outputToConsole(
      ConsoleOutputLevel.debug,
      () =>
        `Discarding ${heldBeacons.length} sentinel measurement results due to blocking detection`,
    )
  }

  return {
    detectionAttempted: true,
    blockingDetected,
    sentinelCount: total,
    failureCount,
    failureRate,
    threshold: failureThreshold,
    sentinelPlatforms,
  }
}
